import { useEffect, useRef, useState } from 'react';

import type { Grupo, Membro, Mes, Tarefa } from '../api/tipos';
import { CorpoDaTarefa } from './CorpoDaTarefa';
import { useMutacoesRoadmap } from './roadmapApi';
import './modal.css';

/**
 * O modal da frente, port do protótipo: título editável, bloco, janela de meses,
 * responsáveis toggláveis, conclusão, checklist com edição inline e exclusão.
 */
export function ModalFrente({
  tarefa,
  grupos,
  membros,
  meses,
  fechar,
}: {
  tarefa: Tarefa;
  grupos: Grupo[];
  membros: Membro[];
  meses: Mes[];
  fechar(): void;
}) {
  const { editarTarefa, excluirTarefa } = useMutacoesRoadmap();

  const [nome, setNome] = useState(tarefa.nome);
  const debounceNome = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    function aoTeclar(e: KeyboardEvent) {
      if (e.key === 'Escape') fechar();
    }
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [fechar]);

  const grupo = grupos.find((g) => g.id === tarefa.grupoId);
  const duracao = tarefa.mesFim - tarefa.mesInicio + 1;

  function renomear(valor: string) {
    setNome(valor);
    clearTimeout(debounceNome.current);
    debounceNome.current = setTimeout(() => {
      if (valor.trim() && valor.trim() !== tarefa.nome) {
        editarTarefa.mutate({ id: tarefa.id, nome: valor.trim() });
      }
    }, 500);
  }

  function mudarJanela(inicio: number, fim: number) {
    editarTarefa.mutate({
      id: tarefa.id,
      mesInicio: Math.min(inicio, fim),
      mesFim: Math.max(inicio, fim),
    });
  }

  function alternarResponsavel(membroId: number) {
    const atuais = new Set(tarefa.responsaveis);
    if (atuais.has(membroId)) atuais.delete(membroId);
    else atuais.add(membroId);
    editarTarefa.mutate({ id: tarefa.id, responsaveis: [...atuais] });
  }

  function excluir() {
    if (!confirm(`Excluir a frente "${tarefa.nome}"?\nO checklist dela também será apagado.`)) return;
    excluirTarefa.mutate(tarefa.id, { onSuccess: fechar });
  }

  return (
    <div className="backdrop" onClick={(e) => e.target === e.currentTarget && fechar()}>
      <div className="modal tarefa" role="dialog" aria-modal="true">
        <div className="mhead">
          <div className="mtop">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="mchip">
                <span className="sw" style={{ background: grupo?.cor }} />
                {grupo?.nome}
              </div>
              <input
                className="mtitle-in"
                value={nome}
                onChange={(e) => renomear(e.target.value)}
                placeholder="Nome da frente"
              />
            </div>
            <button className="mclose" onClick={fechar} aria-label="Fechar">
              ×
            </button>
          </div>

        </div>

        <CorpoDaTarefa
          tarefaId={tarefa.id}
          membros={membros}
          descricao={tarefa.descricao}
          aoSalvarDescricao={(descricao) => editarTarefa.mutate({ id: tarefa.id, descricao })}
          concluida={tarefa.concluida}
          aoAlternarConcluida={() =>
            editarTarefa.mutate({ id: tarefa.id, concluida: !tarefa.concluida })
          }
          rotuloConcluida="Frente"
          responsaveis={tarefa.responsaveis}
          aoAlternarResponsavel={alternarResponsavel}
          comentarios={tarefa.comentarios}
          aoExcluir={excluir}
          contexto={
            <>
              <h4>Bloco</h4>
              <select
                className="tf-select"
                value={tarefa.grupoId}
                onChange={(e) =>
                  editarTarefa.mutate({ id: tarefa.id, grupoId: Number(e.target.value) })
                }
              >
                {grupos.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.nome}
                  </option>
                ))}
              </select>

              {/* Início e fim juntos, e a duração embaixo: a janela é uma coisa só, e é
                  ela — não cada ponta — que se confere ao olhar o card. */}
              <h4>Janela</h4>
              <div className="tf-janela">
                <select
                  className="tf-select"
                  value={tarefa.mesInicio}
                  onChange={(e) => mudarJanela(Number(e.target.value), tarefa.mesFim)}
                >
                  {meses.map((m) => (
                    <option key={m.indice} value={m.indice}>
                      {m.rotulo}/{String(m.ano).slice(2)}
                    </option>
                  ))}
                </select>
                <span aria-hidden="true">→</span>
                <select
                  className="tf-select"
                  value={tarefa.mesFim}
                  onChange={(e) => mudarJanela(tarefa.mesInicio, Number(e.target.value))}
                >
                  {meses.map((m) => (
                    <option key={m.indice} value={m.indice}>
                      {m.rotulo}/{String(m.ano).slice(2)}
                    </option>
                  ))}
                </select>
              </div>
              <p className="tf-dur">
                {duracao} {duracao > 1 ? 'meses' : 'mês'}
              </p>
            </>
          }
        />
      </div>
    </div>
  );
}

